import React from 'react';
import { Box, ListItem, makeStyles, Typography } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { CustomTheme } from 'common/ui/interfaces';
import { INote, INoteImt } from 'common/components/PersonProfile/interfaces';
import menuItems from 'common/messages/menuItems';
import { colors, typography } from 'common/ui/theme/default';
import { DEFAULT_DATE_TIME_FORMAT } from 'common/constants/dateFormats';
import useTimezoneMoment from 'common/hooks/useTimezoneMoment';
import { ReactComponent as DeleteIcon } from 'img/icons/trash.svg';
import { ActionItem, ActionsMenu } from 'common/components';
import ActionItemEdit from 'components/ActionItems/ActionItemEdit';
import EmployeeLinkWithAvatar from 'common/components/ProfileLinkWithAvatar/EmployeeLinkWithAvatar';

const useStyles = makeStyles((theme: CustomTheme) => ({
  noteItem: {
    position: 'relative',
    display: 'block',
    padding: theme.spacing(0, 2, 0, 0),
  },
  itemInner: {
    borderTop: '1px solid #ededed',
    padding: '16px 0',
    '& a': {
      display: 'flex',
      padding: '0',
    },
  },
  date: {
    color: colors.text.mediumGray,
    marginRight: theme.spacing(1.5),
  },
  menu: {
    minWidth: 'fit-content',
  },
  avatar: {
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
  title: {
    fontSize: typography.body2.fontSize,
    lineHeight: typography.body2.lineHeight,
    letterSpacing: typography.body2.letterSpacing,
  },
  textWrapper: {
    marginTop: '4px',
  },
  text: {
    opacity: 0.7,
    paddingLeft: theme.spacing(3),
    paddingRight: '50px',
    wordWrap: 'break-word',
    lineHeight: 1.33,
  },
  icon: {
    width: 16,
    height: 16,
  },
}));

interface INoteItemProps {
  note: INoteImt;
  onEditClick: (note: INote) => void;
  onDeleteClick: (note: INote) => void;
}

const NotesItem = React.memo(({ note, onEditClick, onDeleteClick }: INoteItemProps) => {
  const [timezoneMoment] = useTimezoneMoment();
  const classes = useStyles();
  const author = note.get('author');

  return (
    <ListItem className={classes.noteItem}>
      <Box className={classes.itemInner}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {author ? (
            <EmployeeLinkWithAvatar
              employee={author.toJS()}
              avatarClass={classes.avatar}
              titleClass={classes.title}
            />
          ) : null}
          <Box display="flex" justifyContent="center" alignItems="center">
            <Typography component="span" variant="body2" className={classes.date}>
              {timezoneMoment(note.get('createdDate')).format(DEFAULT_DATE_TIME_FORMAT)}
            </Typography>
            <ActionsMenu horizontal className={classes.menu}>
              <ActionItemEdit onClick={() => onEditClick(note.toJS())} />
              <ActionItem
                key="2"
                icon={<DeleteIcon className={classes.icon} />}
                onClick={() => onDeleteClick(note.toJS())}
              >
                <FormattedMessage {...menuItems.delete} />
              </ActionItem>
            </ActionsMenu>
          </Box>
        </Box>

        {note.get('text') ? (
          <Box className={classes.textWrapper}>
            <Typography component="p" variant="body2" className={classes.text}>
              {note.get('text')}
            </Typography>
          </Box>
        ) : null}
      </Box>
    </ListItem>
  );
});

export default NotesItem;
